import type { TFunction } from 'i18next';

import type { SmoltReportWarnings } from '@/domain/smoltReport/types';

export type WarningMessage = {
    key: keyof SmoltReportWarnings;
    message: string;
};

/**
 * Dates the warnings name, formatted for display. Each is absent on reports stored before it was
 * recorded, which keep the wording their flags were computed for.
 */
export interface WarningDates {
    deliveryDate: string;
    /** The last weight measurement day, named by `dataEndsBeforeDelivery`. */
    lastMeasurementDay?: string;
    /** The welfare window's last day, named by `welfareLowCoverage`. */
    welfareWindowEnd?: string;
}

export function computeSmoltReportWarnings(
    warnings: SmoltReportWarnings,
    t: TFunction,
    dates: WarningDates
): WarningMessage[] {
    const { deliveryDate, lastMeasurementDay, welfareWindowEnd } = dates;

    const warningKeyMap: Record<keyof SmoltReportWarnings, string> = {
        weightLowFrequency: t(
            'smoltReport.warnings.weightLowFrequency',
            'Few recent days have weight data and total measurements are low - weight figures may be misleading.'
        ),
        weightDataLatency: t(
            'smoltReport.warnings.weightDataLatency',
            'Last weight reading is more than 3 days before delivery or last feeding date.'
        ),
        dataEndsBeforeDelivery: lastMeasurementDay
            ? t('smoltReport.warnings.dataUpToLastMeasurement', {
                  lastMeasurementDay,
                  deliveryDate,
              })
            : '',
        weightPeriodTooShort: t(
            'smoltReport.warnings.weightPeriodTooShort',
            'Less than 21 days between stocking and delivery - too short a period for reliable weight trends.'
        ),
        welfareLowCoverage: welfareWindowEnd
            ? t('smoltReport.warnings.welfareLowCoverageUpTo', { windowEnd: welfareWindowEnd })
            : t(
                  'smoltReport.warnings.welfareLowCoverage',
                  'Welfare measurements exist on fewer than 5 of the last 7 days before delivery.'
              ),
        welfareInsufficientSample: t(
            'smoltReport.warnings.welfareInsufficientSample',
            'Too few gill lid measurements - scores for this trait may be unreliable.'
        ),
    };

    return (Object.keys(warnings) as (keyof SmoltReportWarnings)[])
        .filter((key) => warnings[key] && warningKeyMap[key] !== '')
        .map((key) => ({ key, message: warningKeyMap[key] }));
}
